import { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import {
  setSignupData,
  setVerificationStep,
  setLoading,
  setError,
} from "../../store/slices/signupSlice";
import { authService } from "../../services/api";

const VerificationStep = () => {
  const dispatch = useDispatch();
  const { email, phoneNumber, verificationStep, error, invitationContext } =
    useSelector((state) => state.signup);
  const [isOtpError, setIsOtpError] = useState(false);
  const [verificationCode, setVerificationCode] = useState([
    "",
    "",
    "",
    "",
    "",
    "",
  ]);
  // 'sms' | 'email' | null — which resend channel is currently in flight.
  // Per-channel so only the clicked button shows "Sending..." instead of both
  // going gray together.
  const [resending, setResending] = useState(null);
  const [sendingOtp, setSendingOtp] = useState(false);
  // T047: the SMS send itself was refused (e.g. the provider cannot deliver to
  // this number — otp-send answers 400). The e-mail fallback below is normally
  // only reachable from the OTP-entry screen, which this user never reaches,
  // so it has to be offered here too.
  const [otpSendFailed, setOtpSendFailed] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  // Format phone number to include + prefix
  const formattedPhoneNumber = useMemo(() => {
    if (!phoneNumber) return "";
    return phoneNumber.startsWith("+") ? phoneNumber : `+${phoneNumber}`;
  }, [phoneNumber]);

  // Validation checks
  const isEmailValid = useMemo(() => {
    if (!email) return false;

    // More robust email validation that handles + characters
    // First, let's try to fix common issues with + symbols converted to spaces
    let cleanEmail = email;

    // If email contains spaces and looks like it might have had + symbols
    if (email.includes(" ") && email.includes("@")) {
      const [localPart, domain] = email.split("@");
      if (localPart && domain) {
        // Replace spaces with + in the local part only
        cleanEmail = `${localPart.replace(/\s+/g, "+")}@${domain}`;
      }
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const result = cleanEmail && emailRegex.test(cleanEmail);
    console.log("Email validation:", {
      originalEmail: email,
      cleanEmail,
      isValid: result,
    });

    // If we fixed the email, update the state
    if (cleanEmail !== email && result) {
      console.log("Auto-fixing email from", email, "to", cleanEmail);
      // Use a timeout to avoid state update during render
      setTimeout(() => {
        dispatch(setSignupData({ email: cleanEmail }));
      }, 0);
    }

    return result;
  }, [email, dispatch]);

  const isPhoneValid = useMemo(() => {
    // More lenient phone validation - check if phone exists and has reasonable length
    // react-phone-input-2 provides country code + number, so length varies
    const result =
      phoneNumber && phoneNumber.length >= 8 && phoneNumber.length <= 15;
    console.log("Phone validation:", {
      phoneNumber,
      length: phoneNumber?.length,
      isValid: result,
    });
    return result;
  }, [phoneNumber]);

  const maskedInvitationMobile = useMemo(() => {
    if (!invitationContext.mobile) {
      return null;
    }

    const digitsOnly = invitationContext.mobile.replace(/[^\d]/g, "");
    if (digitsOnly.length <= 4) {
      return invitationContext.mobile;
    }

    const lastFour = digitsOnly.slice(-4);
    return `******${lastFour}`;
  }, [invitationContext.mobile]);

  const isVerificationCodeComplete = useMemo(() => {
    return verificationCode.every((code) => code !== "");
  }, [verificationCode]);

  const isInitialFormValid = isEmailValid && isPhoneValid;

  console.log("Form validation status:", {
    isEmailValid,
    isPhoneValid,
    isInitialFormValid,
    verificationStep,
  });

  const handleSendVerification = async (e) => {
    e.preventDefault();
    if (!isInitialFormValid) return;
    setSendingOtp(true);

    dispatch(setLoading(true));
    dispatch(setError(null));
    setIsOtpError(false);
    setOtpSendFailed(false);

    const response = await authService.sendOTP({
      email,
      phoneNumber: formattedPhoneNumber, // Send formatted number with +
      invite_token: invitationContext?.token || null,
    });

    if (response.success) {
      dispatch(setVerificationStep("verification"));
    } else {
      // The backend now answers 400 with a specific reason instead of a bare
      // 500 (BE T047); show it, and open the e-mail fallback from here.
      dispatch(setError(response.error));
      setOtpSendFailed(true);
    }

    dispatch(setLoading(false));
    setSendingOtp(false);
  };

  const handleVerificationComplete = async (e) => {
    if (e) e.preventDefault();
    if (!isVerificationCodeComplete) return;

    setVerifyingOtp(true);
    dispatch(setError(null));
    setIsOtpError(false);

    try {
      const response = await authService.verifyOTP({
        otp: verificationCode.join(""),
        phoneNumber: formattedPhoneNumber,
      });

      if (response.success) {
        dispatch(setVerificationStep("password"));
      } else {
        dispatch(setError(response.error));
        setVerificationCode(["", "", "", "", "", ""]);
      }
    } catch (error) {
      dispatch(setError("Verification failed. Please try again."));
      setVerificationCode(["", "", "", "", "", ""]);
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleResendCode = async () => {
    if (resending) return;
    setResending("sms");
    dispatch(setError(null));
    setIsOtpError(false);
    const response = await authService.sendOTP({
      email,
      phoneNumber: formattedPhoneNumber, // Send formatted number with +
      invite_token: invitationContext?.token || null,
    });

    if (response.success) {
      setVerificationCode(["", "", "", "", "", ""]);
      document.getElementById("code-0")?.focus();
    } else {
      dispatch(setError(response.error));
    }

    setResending(null);
  };

  // Email-channel fallback: triggers when user can't receive SMS (some carriers / countries).
  const handleResendByEmail = async () => {
    if (resending) return;
    setResending("email");
    dispatch(setError(null));
    setIsOtpError(false);
    try {
      const response = await authService.resendSignupOtpByEmail({
        email,
        phoneNumber: formattedPhoneNumber,
      });
      if (response?.success) {
        setVerificationCode(["", "", "", "", "", ""]);
        setOtpSendFailed(false);
        // Reached from a failed SMS send, the code screen has not been shown
        // yet — the e-mail OTP is on its way, so open it now.
        if (verificationStep !== "verification") {
          dispatch(setVerificationStep("verification"));
        }
        document.getElementById("code-0")?.focus();
      } else {
        dispatch(setError(response?.error || "Failed to resend code via email"));
      }
    } catch (err) {
      console.error("Resend by email error:", err);
      dispatch(setError("Something went wrong. Please try again."));
    } finally {
      setResending(null);
    }
  };

  // The e-mail-channel fallback action, offered in the two places a user can
  // need it: on the OTP-entry screen, and (T047) right after an SMS send the
  // provider refused — the only exit for a number that cannot receive SMS.
  const renderSendByEmailButton = () => (
    <button
      type="button"
      onClick={handleResendByEmail}
      disabled={!!resending}
      className="text-[#0066FF] hover:underline disabled:opacity-50 disabled:no-underline"
    >
      {resending === "email" ? "Sending email..." : "Send by Email"}
    </button>
  );

  const handleCodeChange = (index, value) => {
    if (value.length <= 1) {
      const newCode = [...verificationCode];
      newCode[index] = value;
      setVerificationCode(newCode);

      if (value && index < 5) {
        document.getElementById(`code-${index + 1}`).focus();
      }

      // Only auto-submit if all codes are filled
      if (index === 5 && value && newCode.every((code) => code !== "")) {
        handleVerificationComplete();
      }
    }
  };

  useEffect(() => {
    if (error && error === "Invalid OTP.") {
      setIsOtpError(true);
    }
  }, [error]);

  return (
    <>
      <div className="mb-[100px] md:mb-0">
        <h2 className="text-[23.3px] leading-[28px] font-semibold text-[#111927]  md:mb-2">
          Register account
        </h2>
        <p className="text-[14px] leading-[21.98px] text-[#6C737F] mb-8">
          Already have an account?
          <Link
            to="/login"
            className="text-[#2283FF] hover:text-blue-700 ml-1 font-[500]"
          >
            Log in
          </Link>
        </p>
      </div>

      {error && !isOtpError && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <form
        onSubmit={
          verificationStep === "initial"
            ? handleSendVerification
            : handleVerificationComplete
        }
        className="space-y-6 flex flex-col gap-3"
      >
        <div>
          <label className="block text-[16px] font-medium text-black mb-3">
            Email address
          </label>
          <input
            type="email"
            className={`w-full p-4 border border-gray-300 placeholder:text-black/60 placeholder:font-medium placeholder:text-[16px] placeholder:leading-[21px] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0066FF] focus:border-[#0066FF] text-black font-medium text-[16px] leading-[21px] h-[48px] ${
              verificationStep === "verification" ||
              invitationContext.isInvitationFlow
                ? "bg-gray-50 text-gray-600"
                : ""
            }`}
            placeholder="Email address"
            value={email}
            disabled={
              verificationStep === "verification" ||
              invitationContext.isInvitationFlow
            }
            onChange={(e) => dispatch(setSignupData({ email: e.target.value }))}
          />
        </div>

        <div>
          <label className="block text-[16px] font-medium text-black mb-3">
            Phone number
          </label>
          <div className="relative">
            <PhoneInput
              country={"fr"}
              value={phoneNumber}
              onChange={(phone) =>
                dispatch(setSignupData({ phoneNumber: phone }))
              }
              disabled={
                verificationStep === "verification" ||
                (invitationContext.isInvitationFlow &&
                  !!invitationContext.mobile)
              }
              enableSearch={true}
              searchPlaceholder="Search country..."
              inputClass={` !w-full !h-[48px] !text-base !border-gray-300 !rounded-lg !pl-[70px] focus:!ring-1 focus:!ring-[#0066FF] focus:!border-[#0066FF] !text-black ${
                verificationStep === "verification" ? "!bg-white" : ""
              }`}
              buttonClass={`!bg-transparent !border-gray-300 !border !rounded-l-lg !border-r-0 !w-[72px] !h-[48px] ${
                verificationStep === "verification" ? "!bg-white" : ""
              }`}
              containerClass="!w-full"
              dropdownClass="!w-[300px] !rounded-lg !mt-1 !border-gray-300 !shadow-lg"
              searchClass="!p-2 !m-0"
              placeholder="294 27 84 11 00"
              specialLabel=""
              preferredCountries={["fr", "gb", "us", "de"]}
              countryCodeEditable={false}
            />
          </div>
          {invitationContext.isInvitationFlow && invitationContext.mobile && (
            <p className="text-sm text-gray-500 mt-2">
              Mobile provided by your agent:{" "}
              <span className="font-medium">{maskedInvitationMobile}</span>
            </p>
          )}
          {otpSendFailed && verificationStep !== "verification" && (
            <div className="mt-2 flex items-center gap-2 text-sm">
              <span className="text-gray-600">
                Can't receive an SMS on this number?
              </span>
              {renderSendByEmailButton()}
            </div>
          )}
        </div>

        {verificationStep === "verification" && (
          <div className="mt-8">
            <label
              className={`block text-[16px] font-medium ${
                isOtpError ? "text-[#FF3B30]" : "text-black"
              } mb-1`}
            >
              Enter Verification code
            </label>
            <p className=" text-[#000000]/60 mb-4 mt-4 text-[13px]">
              We've sent an SMS with an activation code to your phone{" "}
              <span className="text-black font-medium">
                {formattedPhoneNumber}
              </span>
            </p>
            {/* Below sm the 6×44px boxes only fit next to the 40px page
                padding with the tighter 12px gap the login 2FA row uses
                (SignIn.js): 6×44 + 5×12 = 324 ≤ 335. */}
            <div className="flex gap-3 sm:gap-4 mb-6">
              {verificationCode.map((code, index) => (
                <input
                  key={index}
                  id={`code-${index}`}
                  type="text"
                  placeholder="X"
                  inputMode="numeric"
                  pattern="\d*"
                  maxLength="1"
                  className={`w-[44px] h-[44px] text-center border ${
                    isOtpError ? "border-[#FF3B30]" : "border-gray-300"
                  } rounded-xl focus:outline-none focus:ring-1 focus:ring-[#0066FF] focus:border-[#0066FF] text-lg font-medium`}
                  value={code}
                  onChange={(e) => {
                    handleCodeChange(index, e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && !code && index > 0) {
                      document.getElementById(`code-${index - 1}`).focus();
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pastedData = e.clipboardData
                      .getData("text")
                      .slice(0, 6);
                    const pastedArray = pastedData
                      .split("")
                      .map((char) => char.replace(/\D/g, ""));

                    if (pastedArray.length === 6) {
                      setVerificationCode(pastedArray);
                      document.getElementById(`code-5`).focus();
                    }
                  }}
                />
              ))}
            </div>
            {isOtpError && (
              <div className="text-[#FF3B30] text-[12px] mb-4">
                Oops! That code doesn't seem right. Double-check and try again,
                or request a new code.
              </div>
            )}

            <div className="mb-1">
              <div className="text-sm text-gray-600 mb-1">
                Didn't receive a code?
              </div>
              <div className="flex items-center gap-3 text-sm">
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={!!resending}
                  className="text-[#0066FF] hover:underline disabled:opacity-50 disabled:no-underline"
                >
                  {resending === "sms" ? "Sending SMS..." : "Resend SMS"}
                </button>
                <span className="text-gray-300">|</span>
                {renderSendByEmailButton()}
              </div>
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={
            verificationStep === "initial"
              ? !isInitialFormValid || sendingOtp
              : !isVerificationCodeComplete || verifyingOtp
          }
          className={`w-full py-2 px-4 rounded-lg transition-colors ${
            (
              verificationStep === "initial"
                ? isInitialFormValid && !sendingOtp
                : isVerificationCodeComplete && !verifyingOtp
            )
              ? "bg-[#2283FF] hover:bg-[#1a6cd4] text-white"
              : "bg-[#2283FF]/50 text-[#F5F5F5] cursor-not-allowed"
          }`}
        >
          {verificationStep === "initial"
            ? sendingOtp
              ? "Sending..."
              : "Send verification code"
            : verifyingOtp
            ? "Verifying..."
            : "Verify code"}
        </button>
      </form>
    </>
  );
};

export default VerificationStep;
